import {
  IPatientDetails,
  IPrescriberDetails,
  IMedicationDetails,
} from "../types/PrescriptionIngestionTypes";
import {
  generateMessageId,
  convertToPST,
  getPSTTimestamp,
  generateS3Key,
} from "./PrescriptionHelpers";
import { getUniqueName } from "../../../utils/RandomDemographicInfoGen";
import { UPMS_CONFIG } from "../config/UPMSConfig";
import { NEWRX_MESSAGE_CONFIG, PHARMACY_DETAILS } from "../config/PrescriptionDefaults";

// Clinic name applied to the payload when the prescription is flagged as a
// One Medical prescription.
const ONE_MEDICAL_CLINIC_NAME = "One Medical";

interface TransferRxParams {
  patientDetails: IPatientDetails;
  prescriberDetails: IPrescriberDetails;
  medicationDetails: IMedicationDetails;
  placeOfService: string;
  sourcePharmacyNCPDP: string;
  sourcePharmacyNPI: string;
  sourcePharmacyBusinessName: string;
  sourcePharmacyAddressLine1: string;
  sourcePharmacyCity: string;
  sourcePharmacyState: string;
  sourcePharmacyZipCode: string;
  lastFillDate: string;
  refillsRemaining: string;
  quantityRemaining: { value: string; quantityUnitOfMeasure: { code: string }; codeListQualifier: string };
  quantityTransferred: { value: string; quantityUnitOfMeasure: { code: string }; codeListQualifier: string };
  writtenDate: string;
  expirationDate: string;
}

/**
 * Builds a TransferRx payload for UPMS ingestion
 */
export const buildTransferRxPayload = (params: TransferRxParams) => {
  const {
    patientDetails,
    prescriberDetails,
    medicationDetails,
    placeOfService,
    sourcePharmacyNCPDP,
    sourcePharmacyNPI,
    sourcePharmacyBusinessName,
    sourcePharmacyAddressLine1,
    sourcePharmacyCity,
    sourcePharmacyState,
    sourcePharmacyZipCode,
    lastFillDate,
    refillsRemaining,
    quantityRemaining,
    quantityTransferred,
    writtenDate,
    expirationDate,
  } = params;

  const messageId = generateMessageId();
  const pstDateString = convertToPST(new Date());

  return {
    header: {
      to: "5919177",
      from: prescriberDetails.spi,
      messageID: messageId,
      sentTime: pstDateString,
    },
    patient: {
      humanPatient: {
        name: {
          lastName: patientDetails.lastName,
          firstName: patientDetails.firstName,
        },
        gender: patientDetails.gender,
        dateOfBirth: { date: patientDetails.dateOfBirth },
        address: {
          addressLine1: patientDetails.addressLine1,
          addressLine2: patientDetails.addressLine2,
          city: patientDetails.city,
          countryCode: "US",
          stateProvince: patientDetails.state,
          postalCode: patientDetails.zipCode,
        },
        communicationNumbers: {
          primaryTelephone: { number: patientDetails.phoneNumber },
        },
      },
    },
    prescriber: {
      nonVeterinarian: {
        identification: {
          deaNumber: prescriberDetails.deaNumber,
          npi: prescriberDetails.npi,
        },
        name: {
          lastName: prescriberDetails.lastName,
          firstName: prescriberDetails.firstName,
        },
        address: {
          addressLine1: prescriberDetails.addressLine1,
          city: prescriberDetails.city,
          countryCode: "US",
          stateProvince: prescriberDetails.state,
          postalCode: prescriberDetails.zipCode,
        },
        placeOfService: placeOfService,
        communicationNumbers: {
          primaryTelephone: { number: prescriberDetails.phoneNumber },
          ...(prescriberDetails.fax &&
            prescriberDetails.fax.length > 0 && {
            fax: prescriberDetails.fax.map((f) => ({ number: f.Number })),
          }),
        },
      },
    },
    pharmacist: {
      name: { firstName: getUniqueName(), lastName: getUniqueName() },
    },
    pharmacy: UPMS_CONFIG.PHARMACY,
    sourcePharmacy: {
      identification: {
        ncpdpId: sourcePharmacyNCPDP,
        npi: sourcePharmacyNPI,
      },
      businessName: sourcePharmacyBusinessName,
      address: {
        addressLine1: sourcePharmacyAddressLine1,
        city: sourcePharmacyCity,
        countryCode: "US",
        stateProvince: sourcePharmacyState,
        postalCode: sourcePharmacyZipCode,
      },
    },
    medicationTransferred: {
      medicationDispensed: [
        {
          drugDescription: medicationDetails.drugDescription,
          drugCoded: {
            productCode: { code: medicationDetails.ndc, qualifier: "ND" },
            strength: {
              strengthValue: `${medicationDetails.strength?.value} ${medicationDetails.strength?.unitOfMeasureDescription}`,
              strengthForm: {
                description:
                  medicationDetails.strength?.formDescription || "TABLET",
              },
            },
          },
          quantityReceived: {
            value: medicationDetails.daysSupply,
            quantityUnitOfMeasure: { code: "C120263" },
            codeListQualifier: "87",
          },
          daysSupply: medicationDetails.daysSupply,
          sig: { sigText: medicationDetails.sigText },
          lastFillDate: { date: lastFillDate },
          substitutions:
            medicationDetails.dispenseAsWritten?.toString() || "0",
        },
      ],
      prescriptionPreviouslyFilled: true,
    },
    medicationPrescribed: {
      drugDescription: medicationDetails.drugDescription,
      drugCoded: {
        productCode: { code: medicationDetails.ndc, qualifier: "ND" },
        strength: {
          strengthValue: `${medicationDetails.strength?.value} ${medicationDetails.strength?.unitOfMeasureDescription}`,
          strengthForm: {
            description:
              medicationDetails.strength?.formDescription || "TABLET",
          },
        },
      },
      quantity: {
        value: medicationDetails.quantity?.value,
        quantityUnitOfMeasure: {
          code: medicationDetails.quantity?.unitOfMeasureCode || "C120263",
        },
        codeListQualifier: medicationDetails.quantity?.qualifierCode || "38",
      },
      daysSupply: medicationDetails.daysSupply,
      sig: { sigText: medicationDetails.sigText },
      numberOfRefills: medicationDetails.numberOfRefills,
      substitutions: medicationDetails.dispenseAsWritten?.toString() || "0",
      expirationDate: { date: expirationDate },
      writtenDate: { date: writtenDate },
      startDate: { date: writtenDate },
      effectiveDate: { date: writtenDate },
      refillsRemaining: refillsRemaining,
      quantityRemaining: quantityRemaining,
      quantityTransferred: quantityTransferred,
    },
  };
};


interface RenewalRxParams {
  patientDetails: IPatientDetails;
  prescriberDetails: IPrescriberDetails;
  medicationDetails: IMedicationDetails;
  placeOfService: string;
  /** The original NewRx MessageID this renewal responds to (header.relatesToMessageId). */
  relatesToMessageId: string;
  /** Rx reference number, typically RX<originalMessageId> (header.rxReferenceNumber / approved.referenceNumber). */
  rxReferenceNumber: string;
  /** Renewal written date — must differ from the original NewRx or CATS won't autotype. */
  writtenDate: string;
  /** Approval note (renewal response is an approval). */
  approvalNote: string;
  /** Shared with the S3 file so the envelope header.messageId matches the file's MessageID. */
  messageId: string;
  /** Shared with the S3 file so SentTime matches. */
  sentTime: string;
}

/**
 * Builds a RxRenewalResponse payload for UPMS ingestion (camelCase UPMS shape).
 * Header carries relatesToMessageId + rxReferenceNumber; from = prescriber SPI so it matches the
 * original NewRx sender (required for CATS autotype). Response is an approval.
 * messageId/sentTime are passed in (not generated here) so the ingest envelope and the S3 file
 * (buildRenewalRxFileForS3) share the same identifiers.
 */
export const buildRenewalRxPayload = (params: RenewalRxParams) => {
  const {
    patientDetails,
    prescriberDetails,
    medicationDetails,
    placeOfService,
    relatesToMessageId,
    rxReferenceNumber,
    writtenDate,
    approvalNote,
    messageId,
    sentTime,
  } = params;

  const pstDateString = sentTime;

  return {
    header: {
      to: { identifier: UPMS_CONFIG.FROM_IDENTIFIER, qualifier: UPMS_CONFIG.FROM_QUALIFIER },
      from: { identifier: prescriberDetails.spi, qualifier: UPMS_CONFIG.FROM_QUALIFIER },
      messageId: messageId,
      relatesToMessageId: relatesToMessageId,
      sentTime: pstDateString,
      senderSoftware: {
        senderSoftwareDeveloper: NEWRX_MESSAGE_CONFIG.HEADER.SENDER_SOFTWARE.DEVELOPER,
        senderSoftwareProduct: NEWRX_MESSAGE_CONFIG.HEADER.SENDER_SOFTWARE.PRODUCT,
        senderSoftwareVersionRelease: NEWRX_MESSAGE_CONFIG.HEADER.SENDER_SOFTWARE.VERSION,
      },
      rxReferenceNumber: rxReferenceNumber,
      prescriberOrderNumber: `ORD${relatesToMessageId}`,
    },
    response: {
      approved: { note: approvalNote, referenceNumber: rxReferenceNumber },
    },
    patient: {
      humanPatient: {
        name: { lastName: patientDetails.lastName, firstName: patientDetails.firstName },
        gender: patientDetails.gender,
        dateOfBirth: { date: patientDetails.dateOfBirth },
        address: {
          addressLine1: patientDetails.addressLine1,
          ...(patientDetails.addressLine2 && { addressLine2: patientDetails.addressLine2 }),
          city: patientDetails.city,
          stateProvince: patientDetails.state,
          postalCode: patientDetails.zipCode,
          countryCode: "US",
        },
        communicationNumbers: {
          primaryTelephone: { number: patientDetails.phoneNumber },
        },
      },
    },
    pharmacy: UPMS_CONFIG.PHARMACY,
    prescriber: {
      nonVeterinarian: {
        identification: {
          deaNumber: prescriberDetails.deaNumber,
          npi: prescriberDetails.npi,
        },
        practiceLocation: { businessName: prescriberDetails.clinicName },
        name: { lastName: prescriberDetails.lastName, firstName: prescriberDetails.firstName },
        address: {
          addressLine1: prescriberDetails.addressLine1,
          city: prescriberDetails.city,
          stateProvince: prescriberDetails.state,
          postalCode: prescriberDetails.zipCode,
          countryCode: "US",
        },
        communicationNumbers: {
          primaryTelephone: { number: prescriberDetails.phoneNumber },
          ...(prescriberDetails.fax &&
            prescriberDetails.fax.length > 0 && {
              fax: prescriberDetails.fax.map((f) => ({ number: f.Number })),
            }),
        },
        placeOfService: placeOfService,
      },
    },
    medicationResponse: {
      drugDescription: medicationDetails.drugDescription,
      drugCoded: {
        productCode: { code: medicationDetails.ndc, qualifier: "ND" },
        strength: {
          strengthValue: medicationDetails.strength?.value,
          strengthForm: {
            code: medicationDetails.strength?.formCode,
            description: medicationDetails.strength?.formDescription,
          },
          strengthUnitOfMeasure: { code: medicationDetails.strength?.unitOfMeasureCode },
        },
        drugDbCode: {
          code: medicationDetails.drugDB?.code,
          qualifier: medicationDetails.drugDB?.qualifier,
        },
      },
      quantity: {
        value: medicationDetails.quantity?.value,
        quantityUnitOfMeasure: { code: medicationDetails.quantity?.unitOfMeasureCode || "C48542" },
        codeListQualifier: medicationDetails.quantity?.qualifierCode || "38",
      },
      daysSupply: medicationDetails.daysSupply,
      sig: { sigText: medicationDetails.sigText },
      numberOfRefills: medicationDetails.numberOfRefills,
      substitutions: medicationDetails.dispenseAsWritten?.toString() || "0",
      writtenDate: { date: writtenDate },
    },
  };
};


/**
 * Builds the RxRenewalResponse eScribe FILE that gets uploaded to S3 (what UPMS re-reads via
 * metadata.sourceFileUrl). This is PascalCase NCPDP shape — the SAME format as the NewRx S3 file
 * (buildNewRxPayload), NOT the camelCase UPMS API body. Per the CCS renewal SOP, the S3 file must
 * be this shape or CATS cannot parse it and the renewal never materializes.
 *
 * Autotype prerequisites baked in: Header.From = prescriber SPI (matches the NewRx file's From),
 * RelatesToMessageID = original NewRx MessageID, WrittenDate distinct from the NewRx.
 */
export const buildRenewalRxFileForS3 = (params: RenewalRxParams) => {
  const {
    patientDetails,
    prescriberDetails,
    medicationDetails,
    placeOfService,
    relatesToMessageId,
    rxReferenceNumber,
    writtenDate,
    messageId,
    sentTime,
  } = params;

  const pstTimestamp = getPSTTimestamp();
  const s3Key = generateS3Key(messageId, pstTimestamp);

  return {
    MessageType: "RxRenewalResponse",
    Header: {
      To: NEWRX_MESSAGE_CONFIG.HEADER.TO,
      From: prescriberDetails.spi,
      MessageID: messageId,
      RelatesToMessageID: relatesToMessageId,
      SentTime: sentTime,
      SenderSoftware: {
        SenderSoftwareDeveloper: NEWRX_MESSAGE_CONFIG.HEADER.SENDER_SOFTWARE.DEVELOPER,
        SenderSoftwareProduct: NEWRX_MESSAGE_CONFIG.HEADER.SENDER_SOFTWARE.PRODUCT,
        SenderSoftwareVersionRelease: NEWRX_MESSAGE_CONFIG.HEADER.SENDER_SOFTWARE.VERSION,
      },
      PrescriberOrderNumber: `ORD${relatesToMessageId}`,
      RxNumber: rxReferenceNumber,
    },
    Version: NEWRX_MESSAGE_CONFIG.VERSION,
    Response: {
      Approved: "",
    },
    Patient: {
      Name: {
        LastName: patientDetails.lastName,
        FirstName: patientDetails.firstName,
      },
      Gender: patientDetails.gender,
      DateOfBirth: {
        Date: patientDetails.dateOfBirth,
      },
      Address: {
        AddressLine1: patientDetails.addressLine1,
        ...(patientDetails.addressLine2 && { AddressLine2: patientDetails.addressLine2 }),
        City: patientDetails.city,
        CountryCode: "US",
        State: patientDetails.state,
        ZipCode: patientDetails.zipCode,
      },
      CommunicationNumbers: {
        PrimaryTelephone: {
          Number: patientDetails.phoneNumber,
        },
      },
    },
    Pharmacy: PHARMACY_DETAILS,
    Prescriber: {
      Identification: {
        DEANumber: prescriberDetails.deaNumber,
        NPI: prescriberDetails.npi,
      },
      PracticeLocation: {
        ClinicName: prescriberDetails.clinicName,
      },
      Name: {
        LastName: prescriberDetails.lastName,
        FirstName: prescriberDetails.firstName,
      },
      Address: {
        AddressLine1: prescriberDetails.addressLine1,
        City: prescriberDetails.city,
        CountryCode: "US",
        State: prescriberDetails.state,
        ZipCode: prescriberDetails.zipCode,
      },
      PlaceOfService: placeOfService,
      CommunicationNumbers: {
        PrimaryTelephone: {
          Number: prescriberDetails.phoneNumber,
        },
        ...(prescriberDetails.fax && { Fax: prescriberDetails.fax }),
      },
    },
    MedicationResponse: {
      DrugDescription: medicationDetails.drugDescription,
      Quantity: {
        Value: medicationDetails.quantity?.value,
        UnitOfMeasureCode: medicationDetails.quantity?.unitOfMeasureCode,
        UnitOfMeasureDescription: medicationDetails.quantity?.unitOfMeasureDescription,
        QualifierCode: medicationDetails.quantity?.qualifierCode,
        QualifierDescription: medicationDetails.quantity?.qualifierDescription,
      },
      DaysSupply: medicationDetails.daysSupply,
      WrittenDate: {
        Date: writtenDate,
      },
      NumberOfRefills: medicationDetails.numberOfRefills,
      SigText: medicationDetails.sigText,
      PrescriberCheckedREMS: medicationDetails.prescriberCheckedREMS,
      NDC: medicationDetails.ndc,
      DrugDB: {
        Code: medicationDetails.drugDB?.code,
        Qualifier: medicationDetails.drugDB?.qualifier,
      },
      Strength: {
        Value: medicationDetails.strength?.value,
        FormCode: medicationDetails.strength?.formCode,
        UnitOfMeasureCode: medicationDetails.strength?.unitOfMeasureCode,
        FormDescription: medicationDetails.strength?.formDescription,
        UnitOfMeasureDescription: medicationDetails.strength?.unitOfMeasureDescription,
      },
      DispenseAsWritten: medicationDetails.dispenseAsWritten,
    },
    s3_key: s3Key,
  };
};


interface NewRxParams {
  patientDetails: IPatientDetails;
  prescriberDetails: IPrescriberDetails;
  medicationDetails: IMedicationDetails;
  placeOfService: string;
  // Optional user-provided PrescriberOrderNumber. When empty, a generated
  // value (`ORD${messageId}`) is used so existing callers keep working.
  prescriberOrderNumber?: string;
  // When true, the prescription is treated as a One Medical prescription:
  // the payload's ClinicName is forced to "One Medical".
  isOneMedical?: boolean;
}

/**
 * Builds a NewRx payload for S3 upload
 */
export const buildNewRxPayload = (params: NewRxParams) => {
  const { patientDetails, prescriberDetails, medicationDetails, placeOfService, prescriberOrderNumber, isOneMedical } = params;

  const messageId = generateMessageId();
  const pstDateString = convertToPST(new Date());
  const pstTimestamp = getPSTTimestamp();
  const s3Key = generateS3Key(messageId, pstTimestamp);

  const clinicName = isOneMedical ? ONE_MEDICAL_CLINIC_NAME : prescriberDetails.clinicName;

  return {
    MessageType: NEWRX_MESSAGE_CONFIG.TYPE,
    Header: {
      To: NEWRX_MESSAGE_CONFIG.HEADER.TO,
      From: prescriberDetails.spi,
      MessageID: messageId,
      SentTime: pstDateString,
      SenderSoftware: {
        SenderSoftwareDeveloper: NEWRX_MESSAGE_CONFIG.HEADER.SENDER_SOFTWARE.DEVELOPER,
        SenderSoftwareProduct: NEWRX_MESSAGE_CONFIG.HEADER.SENDER_SOFTWARE.PRODUCT,
        SenderSoftwareVersionRelease: NEWRX_MESSAGE_CONFIG.HEADER.SENDER_SOFTWARE.VERSION,
      },
      PrescriberOrderNumber: prescriberOrderNumber?.trim() || `ORD${messageId}`,
    },
    Version: NEWRX_MESSAGE_CONFIG.VERSION,
    Patient: {
      Name: {
        LastName: patientDetails.lastName,
        FirstName: patientDetails.firstName,
      },
      Gender: patientDetails.gender,
      DateOfBirth: {
        Date: patientDetails.dateOfBirth,
      },
      Address: {
        AddressLine1: patientDetails.addressLine1,
        ...(patientDetails.addressLine2 && { AddressLine2: patientDetails.addressLine2 }),
        City: patientDetails.city,
        CountryCode: "US",
        State: patientDetails.state,
        ZipCode: patientDetails.zipCode,
      },
      CommunicationNumbers: {
        PrimaryTelephone: {
          Number: patientDetails.phoneNumber,
        },
      },
    },
    Pharmacy: PHARMACY_DETAILS,
    Prescriber: {
      Identification: {
        DEANumber: prescriberDetails.deaNumber,
        NPI: prescriberDetails.npi,
      },
      PracticeLocation: {
        ClinicName: clinicName,
      },
      Name: {
        LastName: prescriberDetails.lastName,
        FirstName: prescriberDetails.firstName,
      },
      Address: {
        AddressLine1: prescriberDetails.addressLine1,
        City: prescriberDetails.city,
        CountryCode: "US",
        State: prescriberDetails.state,
        ZipCode: prescriberDetails.zipCode,
      },
      PlaceOfService: placeOfService,
      CommunicationNumbers: {
        PrimaryTelephone: {
          Number: prescriberDetails.phoneNumber,
        },
        ...(prescriberDetails.fax && { Fax: prescriberDetails.fax }),
      },
    },
    MedicationPrescribed: {
      DrugDescription: medicationDetails.drugDescription,
      drugCoded: {
        productCode: { code: medicationDetails.ndc, qualifier: "ND" },
        strength: {
          strengthValue: `${medicationDetails.strength?.value} ${medicationDetails.strength?.unitOfMeasureDescription}`,
          strengthForm: {
            code: medicationDetails.strength.formCode
          },
          strengthUnitOfMeasure: {
            code: medicationDetails.strength.unitOfMeasureCode
          }
        },
      },
      Quantity: {
        Value: medicationDetails.quantity?.value,
        UnitOfMeasureCode: medicationDetails.quantity?.unitOfMeasureCode,
        UnitOfMeasureDescription: medicationDetails.quantity?.unitOfMeasureDescription,
        QualifierCode: medicationDetails.quantity?.qualifierCode,
        QualifierDescription: medicationDetails.quantity?.qualifierDescription,
      },
      DaysSupply: medicationDetails.daysSupply,
      NumberOfRefills: medicationDetails.numberOfRefills,
      PrescriberCheckedREMS: medicationDetails.prescriberCheckedREMS,
      WrittenDate: {
        Date: medicationDetails.writtenDate,
      },
      NDC: medicationDetails.ndc,
      DrugDB: {
        Code: medicationDetails.drugDB.code,
        Qualifier: medicationDetails.drugDB.qualifier,
      },
      Strength: {
        Value: medicationDetails.strength.value,
        FormCode: medicationDetails.strength.formCode,
        UnitOfMeasureCode: medicationDetails.strength.unitOfMeasureCode,
        FormDescription: medicationDetails.strength.formDescription,
        UnitOfMeasureDescription: medicationDetails.strength.unitOfMeasureDescription,
      },
      SigText: medicationDetails.sigText,
      DispenseAsWritten: medicationDetails.dispenseAsWritten,
      ...(medicationDetails.note && { Note: medicationDetails.note }),
    },
    s3_key: s3Key,
  };
};
